import React, {useMemo} from 'react';
import {Grid} from '@mui/material';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
  BuilderDestinationColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {BuilderParserStep} from '../BuilderParserStep/BuilderParserStep';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {withdrawalFieldsType} from './builderWithdrawalFields';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetBuilder} from '../CreateBuilder/BuilderSourceDestination/useResetBuilder';
import {
  changeWithdrawalDestinationFactory,
  changeWithdrawalSourceFactory,
  selectNewBankDestinationFactory,
  getWithdrawalSortedDestinations,
  withdrawalSources,
  withdrawalDestinations,
} from './BuilderWithdrawalHelpers';
import {WithdrawalSourceParent} from './WithdrawalSourceParent';
import {WithdrawalDestinationParent} from './WithdrawalDestinationParent';
import {BuilderWithdrawalNewAccountPlanForm} from './BuilderWithdrawalDestinationPlanForm';
import {NEW_BANK_ACCOUNT_ID} from '../CreateBuilder/BuilderSourceDestination/builderConstants';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {useWithdrawalPotsQuery} from './useWithdrawalPotsQuery';

export const Withdrawal = ({sourceDestinationLabels}: BuilderProps) => {
  const {sourcePots, destinationPots} = useWithdrawalPotsQuery();
  const {loaded: _loaded, setLoaded: _setLoaded} = useLoadedAutoField();
  useResetBuilder(OVRecommendActionEnum.WITHDRAW);
  const {builderReviewId, isSPPDA} = useBuilderContext();
  const [withdrawalFieldObj, _withdrawMeta, _withdrawHelpers] =
    useAutoField<builderFieldsType['withdraw']>('withdraw');
  const {values} = useAutoForm<builderFieldsType>();
  const [withdrawSourceObj, withdrawSourceMeta, withdrawSourceHelpers] = useAutoField<
    withdrawalFieldsType['source'] | undefined
  >('withdraw.source');
  const [withdrawDestinationObj, withdrawDestinationMeta, withdrawDestinationHelpers] =
    useAutoField<withdrawalFieldsType['destination'] | undefined>('withdraw.destination');
  const {value: withdraw} = withdrawalFieldObj;

  const sources = useMemo(() => {
    return withdrawalSources({
      sourcePots,
      isSppDa: isSPPDA,
      reviewId: builderReviewId,
    });
  }, [sourcePots, isSPPDA, builderReviewId]);
  const changeSource = changeWithdrawalSourceFactory({
    sourcePots,
    withdraw,
    withdrawSourceHelpers,
  });
  const changeDestination = changeWithdrawalDestinationFactory({
    withdraw,
    withdrawDestinationHelpers,
  });
  const selectNewBankDestination = selectNewBankDestinationFactory({
    withdraw,
    withdrawDestinationHelpers,
  });
  const destPots = useMemo(() => {
    const sortedDestinations = getWithdrawalSortedDestinations({
      sourcePots,
      destinationPots,
      withdrawSourceObj,
    });
    return withdrawalDestinations({
      sortedDestinations,
      withdrawSourceObj,
    });
  }, [
    withdrawSourceObj.value?.id,
    withdrawSourceObj.value?.parentPotId,
    sourcePots,
    destinationPots,
  ]);

  const nominatedAccountId = sourcePots.find((x) => withdrawSourceObj.value?.id === x.id)
    ?.nominatedAccount?.id;

  if (!values.mainPlanType) {
    return null;
  }
  return sourcePots?.length === 0 ? (
    <BuilderParserStep errorText="No accounts to withdraw from" />
  ) : (
    <BuilderColumnWrapper>
      <Grid item xs={6} xl={5.8} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={withdrawSourceMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          {sources.map((pot) => (
            <WithdrawalSourceParent
              key={pot.id}
              pot={pot}
              withdrawSourceId={withdrawSourceObj.value?.id}
              greyed={
                pot.id === withdrawDestinationObj.value?.id ||
                pot.id === withdrawDestinationObj.value?.parentPotId
              }
              sameReview={pot.review?.id === builderReviewId}
              handleChange={changeSource}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
      <Grid item xs={6} xl={5.8} data-test="Destination Choices">
        <BuilderDestinationColumnHeader
          toError={withdrawDestinationMeta.error}
          destinationLabel={sourceDestinationLabels?.second}
        />
        <BuilderPlanFormsStyled>
          {destPots.map((pot) => (
            <WithdrawalDestinationParent
              key={pot.id}
              pot={pot}
              withdrawDestinationId={withdrawDestinationObj.value?.id}
              sameReview={pot.review?.id === builderReviewId}
              nominatedAccountId={nominatedAccountId}
              handleChange={changeDestination}
            />
          ))}
          <BuilderWithdrawalNewAccountPlanForm
            isSelected={withdrawDestinationObj.value?.id === NEW_BANK_ACCOUNT_ID}
            handleChange={selectNewBankDestination}
          />
        </BuilderPlanFormsStyled>
      </Grid>
    </BuilderColumnWrapper>
  );
};
